import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Grid, P, TextField, Toggle } from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import { FormGroup } from './StyledComponents';
import FeatureInfoDrawer from './FeatureInfoDrawer';
import { secondsToHumanReadable } from '../../utils/dates';
import {
  MAX_FAILURE_THRESHOLD_LENGTH,
  MAX_RETRY_AFTER_LENGTH,
} from '../../common/constants/maxLengthValues';

const CircuitBreakerSettings = ({
  edit,
  circuitBreakerEnabled,
  circuitBreakerRetryAfter,
  circuitBreakerFailureThreshold,
  warningMessageFailureThresholdMessage,
  handleTextChange,
  setCircuitBreakerEnabled,
}) => {
  const handleRetryAfterChange = (e) => {
    if (e.target.value.length > MAX_RETRY_AFTER_LENGTH) {
      return;
    }

    handleTextChange(e);
  };

  const handleFailureThresholdChange = (e) => {
    if (e.target.value.length > MAX_FAILURE_THRESHOLD_LENGTH) {
      return;
    }

    handleTextChange(e);
  };

  return (
    <>
      <Grid.Column span="2">
        <FeatureInfoDrawer
          heading={<Trans>Circuit Breaker</Trans>}
          lead={<Trans>Enable/Disable the Circuit Breaker</Trans>}
          ariaLabel={t`More information about the Circuit Breaker`}
        >
          <p>
            <Trans>
              When enabled, the Circuit Breaker protects the integration from a
              struggling partner system by pausing outbound traffic once a set
              failure threshold is reached for specific errors: 408, 429, 500,
              502, 503, 504, and 515, plus any unexpected or unclassified
              exception, which defaults to 500 and therefore counts toward the
              threshold.
            </Trans>
          </p>
          <ul>
            <li>
              <Trans>
                Once the failure threshold is reached, the circuit opens and the
                integration stops processing new requests.
              </Trans>
            </li>
            <li>
              <Trans>
                For application events integrations, incoming requests are
                queued and processed automatically once the circuit closes
                again. Nothing is lost, it’s just delayed.
              </Trans>
            </li>
            <li>
              <Trans>
                For HTTP integrations, incoming requests fail immediately and
                are not queued or retried because HTTP calls require a real-time
                response.
              </Trans>
            </li>
            <li>
              <Trans>
                After the retry-after cool-down period ends, the circuit doesn’t
                reopen all at once. It first sends a single trial request to
                check whether the partner has recovered.
              </Trans>
            </li>
            <li>
              <Trans>
                If the trial request succeeds, the circuit closes and normal
                traffic resumes. If it fails, the circuit stays open for another
                cool-down period and tries again with one trial request at a
                time.
              </Trans>
            </li>
          </ul>
        </FeatureInfoDrawer>
      </Grid.Column>
      <Grid.Column>
        {edit ? (
          <FormGroup key="circuitBreaker">
            {circuitBreakerEnabled ? (
              <Grid.Column>
                <span className="spanDetail">
                  <P>Enabled</P>
                </span>
                <span>
                  <P>Yes</P>
                </span>
                <span className="spanDetail">
                  <P>Circuit Breaker Retry After</P>
                </span>
                <span>
                  <P>
                    {circuitBreakerRetryAfter} seconds (
                    {secondsToHumanReadable(circuitBreakerRetryAfter)})
                  </P>
                </span>
                <span className="spanDetail">
                  <P>Circuit Breaker Failure Threshold</P>
                </span>
                <span>
                  <P>{circuitBreakerFailureThreshold}</P>
                </span>
              </Grid.Column>
            ) : (
              <Grid.Column>
                <span className="spanDetail">
                  <P>Enabled</P>
                </span>
                <span>
                  <P>No</P>
                </span>
              </Grid.Column>
            )}
          </FormGroup>
        ) : (
          <FormGroup key="circuitBreakerEnabled">
            <Toggle
              name="toggle_circuit_breaker"
              options={[
                {
                  id: 'disabled',
                  text: t`Disabled`,
                },
                { id: 'enabled', text: t`Enabled` },
              ]}
              onChange={(e) => {
                setCircuitBreakerEnabled(e === 'enabled');
              }}
              selected={circuitBreakerEnabled ? 'enabled' : 'disabled'}
            />
            {circuitBreakerEnabled ? (
              <FormGroup>
                <TextField
                  label={t`Circuit Breaker Retry After in seconds`}
                  name="circuit_breaker_retry_after"
                  required
                  type="number"
                  value={String(circuitBreakerRetryAfter)}
                  onChange={handleRetryAfterChange}
                />
                <Flex marginTop={1}>
                  <TextField
                    label={t`Circuit Breaker Failure Threshold`}
                    name="circuit_breaker_failure_threshold"
                    required
                    type="number"
                    warningMessage={warningMessageFailureThresholdMessage}
                    value={String(circuitBreakerFailureThreshold)}
                    onChange={handleFailureThresholdChange}
                  />
                </Flex>
              </FormGroup>
            ) : null}
          </FormGroup>
        )}
      </Grid.Column>
    </>
  );
};

CircuitBreakerSettings.propTypes = {
  edit: PropTypes.bool.isRequired,
  circuitBreakerEnabled: PropTypes.bool.isRequired,
  circuitBreakerRetryAfter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  circuitBreakerFailureThreshold: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  warningMessageFailureThresholdMessage: PropTypes.string.isRequired,
  handleTextChange: PropTypes.func.isRequired,
  setCircuitBreakerEnabled: PropTypes.func.isRequired,
};

export default CircuitBreakerSettings;
